import type { PlayerParty, PlayerSocialState } from '@/api/public'
import {
  playerPartyInviteAvailability,
  playerPartyInviteUnavailableReason,
  playerSocialStatusPresentation,
  type PlayerSocialStatusPresentation,
} from './playerSocialPresentation.ts'
import type { InjectionKey, ShallowRef } from 'vue'

export type PlayerProfileFriendAction =
  | 'add'
  | 'accept'
  | 'cancel'
  | 'remove'
  | 'blocked'

export type PlayerProfileIgnoreAction = 'ignore' | 'unignore'

export type PlayerProfileRelationship = {
  action: PlayerProfileFriendAction
  resourceID: string
}

export type PlayerProfileSocialActionState = {
  visible: boolean
  loading: boolean
  busy: boolean
  canInvite: boolean
  canMessage: boolean
  showFriendAction: boolean
  friendAction: PlayerProfileFriendAction
  friendActionDisabled: boolean
  ignoreAction: PlayerProfileIgnoreAction | null
  ignoreActionDisabled: boolean
  inviteLabel: string
  messageLabel: string
  friendLabel: string
  ignoreLabel: string
  error: string | null
}

export type PlayerProfilePartyInvitePresentationOptions = {
  party: Pick<PlayerParty, 'members'> | null
  actorSteamID: string
  targetSteamID: string
  loading: boolean
  actionPending: boolean
  targetBlocked: boolean
  rosterLocked: boolean
  rosterLockReason: string
  cooldownSeconds: number
  searchQueued: boolean
}

export type PlayerProfilePartyInvitePresentation = {
  allowed: boolean
  label: string
}

export type PlayerProfileSocialActionsController = {
  stateForPlayer: (steamID: string) => PlayerProfileSocialActionState
  statusForPlayer: (steamID: string) => PlayerSocialStatusPresentation | null
  invitePlayer: (steamID: string) => Promise<boolean>
  messagePlayer: (steamID: string, trigger: HTMLElement | null) => Promise<boolean>
  applyFriendAction: (steamID: string) => Promise<PlayerProfileFriendAction | null>
  applyIgnoreAction: (steamID: string) => Promise<PlayerProfileIgnoreAction | null>
  clearFeedback: () => void
}

export const playerProfileSocialActionsKey: InjectionKey<
  ShallowRef<PlayerProfileSocialActionsController | null>
> = Symbol('player-profile-social-actions')

export function playerProfilePartyInvitePresentation(
  options: PlayerProfilePartyInvitePresentationOptions,
): PlayerProfilePartyInvitePresentation {
  const baseLabel = options.searchQueued
    ? options.party
      ? 'Пригласить в группу — поиск отменится, если игрок примет приглашение'
      : 'Пригласить в группу — текущий поиск будет отменён'
    : 'Пригласить в группу'

  if (options.targetBlocked) return { allowed: false, label: 'Игрок заблокирован' }
  if (options.loading) return { allowed: false, label: 'Состав группы синхронизируется' }

  const availability = playerPartyInviteAvailability(
    options.party,
    options.actorSteamID,
    {
      targetSteamID: options.targetSteamID,
      rosterLocked: options.rosterLocked,
      actionPending: options.actionPending,
      cooldownSeconds: options.cooldownSeconds,
    },
  )
  return {
    allowed: availability.allowed,
    label: playerPartyInviteUnavailableReason(
      availability,
      options.rosterLockReason,
    ) || baseLabel,
  }
}

export function playerProfileRelationship(
  state: PlayerSocialState | null,
  steamID: string,
): PlayerProfileRelationship {
  if (!state || !steamID) return { action: 'add', resourceID: '' }

  const block = state.blocks.find(item => item.player.steam_id === steamID)
  if (block) return { action: 'blocked', resourceID: block.id }

  const friend = state.friends.find(item => item.player.steam_id === steamID)
  if (friend) return { action: 'remove', resourceID: friend.id }

  const incoming = state.incoming_requests.find(
    item => item.player.steam_id === steamID,
  )
  if (incoming) return { action: 'accept', resourceID: incoming.id }

  const outgoing = state.outgoing_requests.find(
    item => item.player.steam_id === steamID,
  )
  if (outgoing) return { action: 'cancel', resourceID: outgoing.id }

  return { action: 'add', resourceID: '' }
}

export function playerProfileSocialStatus(
  state: PlayerSocialState | null,
  actorSteamID: string,
  targetSteamID: string,
): PlayerSocialStatusPresentation | null {
  const actor = actorSteamID.trim()
  const target = targetSteamID.trim()
  if (!state || !actor || !target) return null
  if (target === actor) {
    return playerSocialStatusPresentation(
      state.self_presence,
      state.self_game_activity,
    )
  }
  const friend = state.friends.find(item => item.player.steam_id === target)
  if (!friend) return null
  return playerSocialStatusPresentation(friend.presence, friend.game_activity)
}

export function playerProfileIgnoreAction(
  friendAction: PlayerProfileFriendAction,
  notificationIgnored = false,
): PlayerProfileIgnoreAction | null {
  if (notificationIgnored) return 'unignore'
  if (friendAction === 'remove' || friendAction === 'blocked') return null
  return 'ignore'
}

export function unavailablePlayerProfileSocialActionState(): PlayerProfileSocialActionState {
  return {
    visible: false,
    loading: false,
    busy: false,
    canInvite: false,
    canMessage: false,
    showFriendAction: false,
    friendAction: 'add',
    friendActionDisabled: true,
    ignoreAction: null,
    ignoreActionDisabled: true,
    inviteLabel: 'Пригласить в группу',
    messageLabel: 'Написать сообщение',
    friendLabel: 'Добавить в друзья',
    ignoreLabel: 'Игнорировать',
    error: null,
  }
}
